import type {
  SignInResponse,
  SuggestedQuestion,
  UserProfile,
  UserSettings,
  UserStatsResponse,
  VerifyGeminiKeyResponse,
} from "@scoach/types";
import { api } from "../../lib/http.ts";

export const authApi = {
  fetchMe: () => api<SignInResponse["user"]>("/users/me"),

  fetchStats: () => api<UserStatsResponse>("/users/me/stats"),

  updateProfile: (data: Partial<UserProfile>) =>
    api<unknown>("/user/profile", { method: "PUT", body: data }),

  updateSettings: (data: Partial<UserSettings>) =>
    api<unknown>("/user/settings", { method: "PUT", body: data }),

  /** GDPR/CCPA portability: fetch all of the caller's data as JSON. */
  exportData: () => api<unknown>("/users/me/export"),

  /** GDPR/CCPA erasure: delete the caller's account and all their data. */
  deleteAccount: () => api<{ ok: boolean; meetings: number }>("/users/me", { method: "DELETE" }),

  /** Remember (key) or forget (null) the user's Gemini key server-side. */
  saveGeminiKey: (key: string | null) =>
    api<void>("/user/gemini-key", { method: "PUT", body: { key } }),

  /** Record how the user obtained their key during onboarding (surfaced to admins). */
  setApiKeySource: (source: "personal" | "none" | "shared" | "managed-project") =>
    api<void>("/users/me/api-key-source", { method: "PATCH", body: { source } }),

  /** Questions the rep asks repeatedly across recent meetings (suggested to save). */
  fetchSuggestedQuestions: () =>
    api<{ suggestions: SuggestedQuestion[] }>("/users/me/suggested-questions").then(
      (r) => r.suggestions ?? [],
    ),

  verifyGeminiKey: (key: string) =>
    api<VerifyGeminiKeyResponse>("/auth/verify-gemini-key", {
      method: "POST",
      body: { key },
    }),

  /** Mint (or re-fetch) a managed Gemini API key for the current user (app project). */
  getManagedKey: () => api<{ key: string }>("/users/me/gemini-key", { method: "POST" }),

  /** Create a Gemini key on the user's OWN GCP project using their Cloud OAuth token. */
  createKeyOnMyProject: (projectId: string, gcpToken: string) =>
    api<{ key: string }>("/users/me/gemini-key/byo", {
      method: "POST",
      body: { projectId },
      headers: { "X-GCP-Token": gcpToken },
    }),

  signOut: () => api<void>("/auth/signout", { method: "POST" }),
};
